// frontend/src/pages/Entities/EntityDetail.jsx
import { useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../hooks/useAuth";
import {
  getEntity,
  uploadEntityDocument,
  updateEntityDocument,
  deleteEntityDocument,
  getEntityDocumentUrl,
  updateEntityInfo,
  updateShareholders,
  updatePengurus,
  getEntityEmailPassword,
  updateEntityIdentity,
  deactivateEntity,
} from "../../api/entity.api.js";
import { getVendors } from "../../api/vendor.api.js";
import { getPartners } from "../../api/partner.api.js";
import {
  PageHeader,
  Spinner,
  DocTypeLabel,
  formatDate,
  formatFileSize,
  ErrorMessage,
} from "../../components/ui";
import {
  ChevronLeft,
  Upload,
  Download,
  Pencil,
  X,
  FileText,
  Save,
  Users,
  Users2,
  Trash2,
  Plus,
  PieChart,
  AlignLeft,
  Eye,
  EyeOff,
  KeyRound,
  ShieldCheck,
  PowerOff,
} from "lucide-react";
import DocPreviewModal from "../../components/DocPreviewModal.jsx";
import { API_URL } from "../../api/client.js";

// ─── Doc type config ──────────────────────────────────────────────────────────
const GENERAL_TYPES = ["AKTA", "NPWP", "AKTA_PERUBAHAN", "SK_KEMENKUMHAM"];
const PERIZINAN_TYPES = [
  "HAKI", "NIB", "PKKPR", "SERTIFIKAT_HALAL", "PBG", "SLF",
  "LEGALITAS_LAHAN", "LAPORAN_RUPS_LUAR_BIASA", "LAPORAN_RUPS_TAHUNAN",
];
const SINGLE_FILE_TYPES = ["AKTA", "NPWP"]; // all others are multi-file

// ─── Pengurus config ──────────────────────────────────────────────────────────
const PENGURUS_ROLES = [
  { key: "komisarisUtama", label: "Komisaris Utama" },
  { key: "komisaris",      label: "Komisaris" },
  { key: "direkturUtama",  label: "Direktur Utama" },
  { key: "direktur",       label: "Direktur" },
];

// Returns { name: string, startDate: string|null } per role — handles old string format
function normalizePengurus(raw = {}) {
  const out = {};
  for (const { key } of PENGURUS_ROLES) {
    const v = raw[key];
    if (!v) { out[key] = { name: "", startDate: null }; continue; }
    if (typeof v === "string") { out[key] = { name: v, startDate: null }; continue; }
    if (Array.isArray(v)) { out[key] = { name: v[0] ?? "", startDate: null }; continue; }
    out[key] = { name: v.name ?? "", startDate: v.startDate ?? null };
  }
  return out;
}

function calcTenure(startDate) {
  if (!startDate) return null;
  const start = new Date(startDate);
  const now = new Date();
  const totalMonths =
    (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
  if (totalMonths < 1) return "< 1 bulan";
  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;
  if (years === 0) return `${months} bulan`;
  if (months === 0) return `${years} tahun`;
  return `${years} thn ${months} bln`;
}

// ─── Pie chart colors ─────────────────────────────────────────────────────────
const CHART_COLORS = [
  "#6366f1",
  "#8b5cf6",
  "#06b6d4",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#ec4899",
  "#14b8a6",
  "#a78bfa",
  "#34d399",
];

// ─── Interactive shareholder pie chart ───────────────────────────────────────

function ShareholderPieChart({ shareholders }) {
  const [hovered, setHovered] = useState(null);
  const [pinned, setPinned] = useState(null);

  if (!shareholders?.length) return null;

  const active = pinned ?? hovered;
  const size = 200,
    cx = 100,
    cy = 100,
    R = 80,
    ri = 44;
  const total = shareholders.reduce((s, sh) => s + Number(sh.percentage), 0);

  let cum = 0;
  const slices = shareholders.map((s, i) => {
    const start = cum / total;
    cum += Number(s.percentage);
    return {
      ...s,
      start,
      end: cum / total,
      color: CHART_COLORS[i % CHART_COLORS.length],
      index: i,
    };
  });

  function polar(pct, r) {
    const a = pct * 2 * Math.PI - Math.PI / 2;
    return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
  }

  function arcPath(start, end, r, ri) {
    if (end - start >= 0.9999) {
      const m1 = polar(0, r),
        m2 = polar(0.5, r);
      const i1 = polar(0, ri),
        i2 = polar(0.5, ri);
      return `M ${m1.x} ${m1.y} A ${r} ${r} 0 0 1 ${m2.x} ${m2.y} A ${r} ${r} 0 0 1 ${m1.x} ${m1.y} L ${i1.x} ${i1.y} A ${ri} ${ri} 0 0 0 ${i2.x} ${i2.y} A ${ri} ${ri} 0 0 0 ${i1.x} ${i1.y} Z`;
    }
    const lg = end - start > 0.5 ? 1 : 0;
    const s1 = polar(start, r),
      s2 = polar(end, r);
    const e1 = polar(end, ri),
      e2 = polar(start, ri);
    return `M ${s1.x} ${s1.y} A ${r} ${r} 0 ${lg} 1 ${s2.x} ${s2.y} L ${e1.x} ${e1.y} A ${ri} ${ri} 0 ${lg} 0 ${e2.x} ${e2.y} Z`;
  }

  function handleEnter(s) {
    if (pinned === null) setHovered(s.index);
  }
  function handleLeave() {
    if (pinned === null) setHovered(null);
  }
  function handleClick(s) {
    if (pinned === s.index) {
      setPinned(null);
      setHovered(null);
    } else {
      setPinned(s.index);
      setHovered(null);
    }
  }

  const activeSlice = active !== null ? slices[active] : null;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-6 items-start flex-wrap">
        {/* Pie */}
        <div className="flex-shrink-0">
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            {slices.map((s) => {
              const isActive = active === s.index;
              const isDimmed = active !== null && !isActive;
              return (
                <path
                  key={s.index}
                  d={arcPath(s.start, s.end, R, ri)}
                  fill={s.color}
                  stroke="white"
                  strokeWidth={1.5}
                  opacity={isDimmed ? 0.2 : 1}
                  style={{
                    cursor: "pointer",
                    filter: isActive ? "brightness(1.1)" : "none",
                    transition: "opacity 0.15s",
                  }}
                  onMouseEnter={() => handleEnter(s)}
                  onMouseLeave={handleLeave}
                  onClick={() => handleClick(s)}
                />
              );
            })}
            <text
              x={cx}
              y={cy - 7}
              textAnchor="middle"
              fontSize={10}
              fill="#9ca3af"
            >
              {activeSlice
                ? activeSlice.name.length > 9
                  ? activeSlice.name.slice(0, 9) + "…"
                  : activeSlice.name
                : "Total"}
            </text>
            <text
              x={cx}
              y={cy + 10}
              textAnchor="middle"
              fontSize={15}
              fontWeight="500"
              fill="#111827"
            >
              {activeSlice
                ? `${Number(activeSlice.percentage).toFixed(1)}%`
                : `${total.toFixed(1)}%`}
            </text>
          </svg>
        </div>

        {/* Legend */}
        <div className="flex-1 min-w-0 space-y-1">
          {slices.map((s) => {
            const isActive = active === s.index;
            const isDimmed = active !== null && !isActive;
            return (
              <div
                key={s.index}
                onClick={() => handleClick(s)}
                onMouseEnter={() => handleEnter(s)}
                onMouseLeave={handleLeave}
                className={`flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg cursor-pointer transition-all border ${
                  isActive
                    ? "bg-gray-50 border-gray-200"
                    : "border-transparent hover:bg-gray-50"
                } ${isDimmed ? "opacity-30" : "opacity-100"}`}
              >
                <div
                  className="w-2.5 h-2.5 rounded-sm flex-shrink-0 transition-transform"
                  style={{
                    backgroundColor: s.color,
                    transform: isActive ? "scale(1.3)" : "scale(1)",
                  }}
                />
                <span className="text-xs text-gray-800 flex-1 truncate">
                  {s.name}
                </span>
                {s.type && (
                  <span className="text-xs text-gray-400 flex-shrink-0">
                    {s.type}
                  </span>
                )}
                <span className="text-xs font-medium text-gray-900 flex-shrink-0">
                  {Number(s.percentage).toFixed(1)}%
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Detail panel */}
      <div
        className={`rounded-lg bg-gray-50 border border-gray-100 px-4 py-3 transition-all ${activeSlice ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        style={{ minHeight: "52px" }}
      >
        {activeSlice && (
          <div className="flex flex-wrap gap-x-4 gap-y-1 items-center">
            <span className="text-xs font-semibold text-gray-900">
              {activeSlice.name}
            </span>
            <span className="text-xs text-gray-600">
              {Number(activeSlice.percentage).toFixed(1)}% ownership
            </span>
            {activeSlice.type && (
              <span className="text-xs text-gray-500">
                Type: {activeSlice.type}
              </span>
            )}
            {activeSlice.notes && (
              <span className="text-xs text-gray-500 italic">
                {activeSlice.notes}
              </span>
            )}
            {pinned !== null && (
              <button
                onClick={() => {
                  setPinned(null);
                  setHovered(null);
                }}
                className="text-xs text-brand-500 hover:underline ml-auto"
              >
                Dismiss
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Shareholder edit modal ───────────────────────────────────────────────────

function ShareholderModal({ entityId, initial, onClose, onSaved }) {
  const [rows, setRows] = useState(
    initial?.length
      ? initial.map((s) => ({ ...s }))
      : [{ name: "", percentage: "", type: "", notes: "" }],
  );
  const [error, setError] = useState(null);

  const save = useMutation({
    mutationFn: () => updateShareholders(entityId, rows),
    onSuccess: (data) => {
      onSaved(data.shareholderData);
      onClose();
    },
    onError: (err) => setError(err?.response?.data?.error ?? err.message),
  });

  function updateRow(i, field, value) {
    setRows((prev) =>
      prev.map((r, idx) => (idx === i ? { ...r, [field]: value } : r)),
    );
  }

  const total = rows.reduce((s, r) => s + (Number(r.percentage) || 0), 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">
            Edit Struktur Pemegang Saham
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {error && <ErrorMessage error={error} />}
          <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-gray-400 uppercase tracking-wide pb-1">
            <div className="col-span-4">Name</div>
            <div className="col-span-2">% Share</div>
            <div className="col-span-2">Type</div>
            <div className="col-span-3">Notes</div>
            <div className="col-span-1" />
          </div>
          {rows.map((row, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-start">
              <div className="col-span-4">
                <input
                  className="input text-sm py-1.5"
                  placeholder="Shareholder name"
                  value={row.name}
                  onChange={(e) => updateRow(i, "name", e.target.value)}
                />
              </div>
              <div className="col-span-2">
                <input
                  className="input text-sm py-1.5"
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  placeholder="0"
                  value={row.percentage}
                  onChange={(e) => updateRow(i, "percentage", e.target.value)}
                />
              </div>
              <div className="col-span-2">
                <input
                  className="input text-sm py-1.5"
                  placeholder="e.g. Individual"
                  value={row.type}
                  onChange={(e) => updateRow(i, "type", e.target.value)}
                />
              </div>
              <div className="col-span-3">
                <input
                  className="input text-sm py-1.5"
                  placeholder="Notes"
                  value={row.notes}
                  onChange={(e) => updateRow(i, "notes", e.target.value)}
                />
              </div>
              <div className="col-span-1 flex justify-center pt-1.5">
                <button
                  onClick={() =>
                    setRows((prev) => prev.filter((_, idx) => idx !== i))
                  }
                  className="text-gray-300 hover:text-red-500 transition-colors"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
          <button
            onClick={() =>
              setRows((prev) => [
                ...prev,
                { name: "", percentage: "", type: "", notes: "" },
              ])
            }
            className="flex items-center gap-1.5 text-sm text-brand-500 hover:text-brand-700 mt-2"
          >
            <Plus size={14} /> Add shareholder
          </button>
        </div>
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between gap-3">
          <div
            className={`text-sm font-medium ${total > 100 ? "text-red-600" : total === 100 ? "text-green-600" : "text-gray-500"}`}
          >
            Total: {total.toFixed(1)}%{total > 100 && " — exceeds 100%"}
            {total === 100 && " ✓"}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="btn-secondary text-sm">
              Cancel
            </button>
            <button
              onClick={() => {
                setError(null);
                save.mutate();
              }}
              disabled={save.isPending || rows.length === 0}
              className="btn-primary flex items-center gap-2 text-sm"
            >
              <Save size={14} /> {save.isPending ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Pengurus modal ───────────────────────────────────────────────────────────

function PengurusModal({ entityId, initial, onClose, onSaved }) {
  // { key: { name: string, startDate: string|null } }
  const [data, setData] = useState(() =>
    Object.fromEntries(
      PENGURUS_ROLES.map(({ key }) => [
        key,
        { name: initial?.[key]?.name ?? "", startDate: initial?.[key]?.startDate ?? "" },
      ])
    )
  );
  const [focusedRole, setFocusedRole] = useState(null);
  const [error, setError] = useState(null);

  function setField(key, field, value) {
    setData((prev) => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
  }

  function suggestionsFor(roleKey) {
    return [...new Set(
      PENGURUS_ROLES
        .filter((r) => r.key !== roleKey)
        .map((r) => data[r.key]?.name?.trim())
        .filter(Boolean),
    )].filter((n) =>
      data[roleKey].name === "" || n.toLowerCase().includes(data[roleKey].name.toLowerCase()),
    );
  }

  const save = useMutation({
    mutationFn: () => updatePengurus(entityId, data),
    onSuccess: (res) => {
      onSaved(res.pengurusData, res.pengurusHistory);
      onClose();
    },
    onError: (err) => setError(err?.response?.data?.error ?? err.message),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">Edit Struktur Pengurus</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {error && <ErrorMessage error={error} />}

          {PENGURUS_ROLES.map(({ key, label }) => {
            const suggestions = suggestionsFor(key);
            const showDropdown = focusedRole === key && suggestions.length > 0;
            return (
              <div key={key}>
                <label className="label text-xs mb-1">{label}</label>
                <div className="flex gap-2">
                  {/* Name input */}
                  <div className="relative flex-1">
                    <input
                      className="input text-sm"
                      placeholder={`Nama ${label}...`}
                      value={data[key].name}
                      onChange={(e) => setField(key, "name", e.target.value)}
                      onFocus={() => setFocusedRole(key)}
                      onBlur={() => setTimeout(() => setFocusedRole(null), 150)}
                      onKeyDown={(e) => {
                        if (e.key === "Escape") { setField(key, "name", ""); setFocusedRole(null); }
                      }}
                    />
                    {data[key].name && (
                      <button
                        onClick={() => setField(key, "name", "")}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500"
                      >
                        <X size={13} />
                      </button>
                    )}
                    {showDropdown && (
                      <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg">
                        <p className="px-3 pt-2 pb-1 text-xs text-gray-400">Gunakan dari jabatan lain:</p>
                        {suggestions.map((s) => (
                          <button
                            key={s}
                            onMouseDown={() => setField(key, "name", s)}
                            className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-brand-50 hover:text-brand-700"
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {/* Start date */}
                  <div className="w-40 flex-shrink-0">
                    <input
                      type="date"
                      className="input text-sm"
                      title="Tanggal mulai menjabat"
                      value={data[key].startDate ?? ""}
                      onChange={(e) => setField(key, "startDate", e.target.value || null)}
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-0.5">Tanggal mulai menjabat (opsional)</p>
              </div>
            );
          })}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary text-sm">
            Batal
          </button>
          <button
            onClick={() => { setError(null); save.mutate(); }}
            disabled={save.isPending}
            className="btn-primary flex items-center gap-2 text-sm"
          >
            <Save size={14} /> {save.isPending ? "Menyimpan..." : "Simpan"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Document section for a single type ──────────────────────────────────────

function DocSection({ type, category, docs, entityId, isMaster, onUploaded, onDeleted }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [validUntil, setValidUntil] = useState("");
  const isMulti = !SINGLE_FILE_TYPES.includes(type);
  const isPerizinan = category === "PERIZINAN";

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("type", type);
      if (validUntil) fd.append("validUntil", validUntil);
      await uploadEntityDocument(entityId, fd);
      onUploaded();
      setValidUntil("");
      setShowUploadForm(false);
    } catch (err) {
      setUploadError(err?.response?.data?.error ?? err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  function triggerUpload() {
    if (isPerizinan) {
      setShowUploadForm((v) => !v);
    } else {
      fileRef.current?.click();
    }
  }

  const deleteMutation = useMutation({
    mutationFn: (docId) => deleteEntityDocument(entityId, docId),
    onSuccess: onDeleted,
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
          <DocTypeLabel type={type} />
          {isMulti && (
            <span className="ml-1.5 text-gray-300 font-normal normal-case">
              ({docs.length} file{docs.length !== 1 ? "s" : ""})
            </span>
          )}
        </p>
        {isMaster && (isMulti || docs.length === 0) && (
          <>
            <button
              onClick={triggerUpload}
              disabled={uploading}
              className="flex items-center gap-1 text-xs text-brand-500 hover:text-brand-700 font-medium"
            >
              <Upload size={12} /> {uploading ? "Uploading..." : "Upload"}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.docx"
              className="hidden"
              onChange={handleFile}
            />
          </>
        )}
      </div>

      {/* Inline upload form for perizinan (shows berlakuSampai date field) */}
      {isPerizinan && showUploadForm && isMaster && (
        <div className="mb-3 p-3 border border-brand-200 bg-brand-50 rounded-lg">
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <label className="label text-xs">Berlaku s/d <span className="text-gray-400 font-normal">(opsional)</span></label>
              <input
                type="date"
                className="input text-sm"
                value={validUntil}
                onChange={(e) => setValidUntil(e.target.value)}
              />
            </div>
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="btn-primary text-xs py-2 px-4 flex items-center gap-1.5 flex-shrink-0"
            >
              <Upload size={13} /> {uploading ? "Uploading..." : "Pilih File"}
            </button>
          </div>
        </div>
      )}

      {uploadError && (
        <p className="text-xs text-red-600 mb-2">{uploadError}</p>
      )}

      {docs.length === 0 ? (
        <div className="flex items-center justify-between p-3 border border-dashed border-gray-200 rounded-lg">
          <p className="text-xs text-gray-400 italic">Belum ada dokumen</p>
          {isMaster && !isMulti && !isPerizinan && (
            <>
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-1 text-xs text-brand-500 hover:text-brand-700"
              >
                <Upload size={12} /> {uploading ? "Uploading..." : "Upload"}
              </button>
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.docx"
                className="hidden"
                onChange={handleFile}
              />
            </>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {docs.map((doc) => (
            <DocRow
              key={doc.id}
              doc={doc}
              entityId={entityId}
              isMaster={isMaster}
              onDelete={() => deleteMutation.mutate(doc.id)}
              onUpdated={onDeleted}
              deleting={deleteMutation.isPending}
            />
          ))}
          {isMaster && !isMulti && docs.length > 0 && (
            <>
              <button
                onClick={triggerUpload}
                disabled={uploading}
                className="flex items-center gap-1 text-xs text-gray-400 hover:text-brand-500 mt-1"
              >
                <Upload size={11} />{" "}
                {uploading ? "Uploading..." : "Replace file"}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Document row ─────────────────────────────────────────────────────────────

function DocRow({ doc, entityId, isMaster, onDelete, onUpdated, deleting }) {
  const [loading, setLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [preview, setPreview] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editValidUntil, setEditValidUntil] = useState(
    doc.validUntil ? new Date(doc.validUntil).toISOString().slice(0, 10) : ""
  );
  const [editCategory, setEditCategory] = useState(doc.category ?? "GENERAL");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const isEditable = doc.category === "PERIZINAN" || doc.type === "CUSTOM";

  async function download() {
    setLoading(true);
    try {
      const { url } = await getEntityDocumentUrl(entityId, doc.id);
      window.open(url, "_blank");
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function saveEdit() {
    setSaving(true);
    setSaveError(null);
    try {
      const payload = { validUntil: editValidUntil || null };
      if (doc.type === "CUSTOM") payload.category = editCategory;
      await updateEntityDocument(entityId, doc.id, payload);
      onUpdated?.();
      setEditing(false);
    } catch (err) {
      setSaveError(err?.response?.data?.error ?? err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg group">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-800 truncate">
          {doc.fileName}
        </p>
        <p className="text-xs text-gray-500 mt-0.5">
          {formatFileSize(doc.fileSize)} · {formatDate(doc.uploadedAt)}
          {doc.validUntil && (
            <span className="ml-1 text-amber-600 font-medium">
              · Berlaku s/d {formatDate(doc.validUntil)}
            </span>
          )}
          {doc.notes && <span className="ml-1 italic">· {doc.notes}</span>}
        </p>
      </div>
      <div className="flex items-center gap-2 ml-3 flex-shrink-0">
        {isMaster && isEditable && (
          <button
            onClick={() => { setEditing((v) => !v); setSaveError(null); }}
            className="p-1.5 rounded-lg text-gray-400 hover:text-brand-600 hover:bg-brand-50 transition-colors"
            title="Edit"
          >
            <Pencil size={13} />
          </button>
        )}
        <button
          onClick={() => setPreview(true)}
          className="p-1.5 rounded-lg text-gray-400 hover:text-brand-600 hover:bg-brand-50 transition-colors"
          title="Preview"
        >
          <Eye size={14} />
        </button>
        <button
          onClick={download}
          disabled={loading}
          className="flex items-center gap-1.5 text-xs btn-secondary py-1.5 px-3"
        >
          <Download size={12} /> {loading ? "..." : "Download"}
        </button>
        {isMaster && !confirmDelete && (
          <button
            onClick={() => setConfirmDelete(true)}
            className="text-gray-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
          >
            <Trash2 size={13} />
          </button>
        )}
        {isMaster && confirmDelete && (
          <div className="flex items-center gap-1">
            <button
              onClick={() => { onDelete(); setConfirmDelete(false); }}
              disabled={deleting}
              className="text-xs text-red-600 hover:underline font-medium"
            >
              {deleting ? "..." : "Delete"}
            </button>
            <button
              onClick={() => setConfirmDelete(false)}
              className="text-xs text-gray-400 hover:text-gray-600"
            >
              <X size={12} />
            </button>
          </div>
        )}
      </div>
    </div>

    {editing && (
      <div className="mt-1 p-3 border border-brand-200 bg-brand-50 rounded-lg">
        <div className="flex gap-2 items-end flex-wrap">
          {doc.type === "CUSTOM" && (
            <div className="flex-1 min-w-32">
              <label className="label text-xs">Kategori</label>
              <select
                className="input text-sm"
                value={editCategory}
                onChange={(e) => setEditCategory(e.target.value)}
              >
                <option value="GENERAL">Dokumen Umum</option>
                <option value="PERIZINAN">Perizinan</option>
                <option value="KEUANGAN">Keuangan</option>
              </select>
            </div>
          )}
          <div className="flex-1 min-w-40">
            <label className="label text-xs">
              Berlaku s/d <span className="text-gray-400 font-normal">(opsional)</span>
            </label>
            <input
              type="date"
              className="input text-sm"
              value={editValidUntil}
              onChange={(e) => setEditValidUntil(e.target.value)}
            />
          </div>
          <div className="flex gap-1.5 pb-0.5">
            <button
              onClick={() => { setEditing(false); setSaveError(null); }}
              className="btn-secondary text-xs py-2 px-3"
            >
              Batal
            </button>
            <button
              onClick={saveEdit}
              disabled={saving}
              className="btn-primary text-xs py-2 px-3 flex items-center gap-1"
            >
              <Save size={12} /> {saving ? "..." : "Simpan"}
            </button>
          </div>
        </div>
        {saveError && <p className="text-xs text-red-600 mt-2">{saveError}</p>}
      </div>
    )}

    {preview && (
      <DocPreviewModal
        fileName={doc.fileName}
        mimeType={doc.mimeType}
        fileSize={doc.fileSize}
        getUrl={() => getEntityDocumentUrl(entityId, doc.id)}
        streamUrl={`${API_URL}/entities/${entityId}/documents/${doc.id}/stream`}
        onClose={() => setPreview(false)}
      />
    )}
    </>
  );
}

// ─── Laporan Keuangan card (standalone multi-file section) ────────────────────

function LaporanKeuanganCard({
  docs,
  entityId,
  isMaster,
  onUploaded,
  onDeleted,
}) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("type", "LAPORAN_KEUANGAN");
      await uploadEntityDocument(entityId, fd);
      onUploaded();
    } catch (err) {
      setUploadError(err?.response?.data?.error ?? err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  const deleteMutation = useMutation({
    mutationFn: (docId) => deleteEntityDocument(entityId, docId),
    onSuccess: onDeleted,
  });

  return (
    <div className="card">
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
        <div>
          <h2 className="font-semibold text-gray-900">Laporan Keuangan</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            {docs.length} file{docs.length !== 1 ? "s" : ""} · multiple uploads
            supported
          </p>
        </div>
        {isMaster && (
          <>
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="btn-primary flex items-center gap-1.5 text-xs py-1.5 px-3"
            >
              <Upload size={13} /> {uploading ? "Uploading..." : "Upload"}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls"
              className="hidden"
              onChange={handleFile}
            />
          </>
        )}
      </div>

      {uploadError && (
        <div className="px-6 pt-4">
          <ErrorMessage error={uploadError} />
        </div>
      )}

      <div className="px-6 py-4">
        {docs.length === 0 ? (
          <div className="text-center py-8 border border-dashed border-gray-200 rounded-lg">
            <FileText size={24} className="text-gray-200 mx-auto mb-2" />
            <p className="text-sm text-gray-400">Belum ada laporan keuangan</p>
            {isMaster && (
              <>
                <button
                  onClick={() => fileRef.current?.click()}
                  className="mt-3 text-xs text-brand-500 hover:underline"
                >
                  Upload laporan keuangan
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls"
                  className="hidden"
                  onChange={handleFile}
                />
              </>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {docs.map((doc) => (
              <DocRow
                key={doc.id}
                doc={doc}
                entityId={entityId}
                isMaster={isMaster}
                onDelete={() => deleteMutation.mutate(doc.id)}
                onUpdated={onDeleted}
                deleting={deleteMutation.isPending}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Custom / other documents card ───────────────────────────────────────────

function CustomDocsCard({ docs, entityId, isMaster, onUploaded, onDeleted }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [customName, setCustomName] = useState("");
  const [category, setCategory] = useState("GENERAL");
  const [validUntil, setValidUntil] = useState("");

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!customName.trim()) {
      setUploadError("Nama dokumen harus diisi");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setUploading(true);
    setUploadError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("type", "CUSTOM");
      fd.append("customName", customName.trim());
      fd.append("category", category);
      if (validUntil) fd.append("validUntil", validUntil);
      await uploadEntityDocument(entityId, fd);
      onUploaded();
      setCustomName("");
      setCategory("GENERAL");
      setValidUntil("");
      setShowForm(false);
    } catch (err) {
      setUploadError(err?.response?.data?.error ?? err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  const deleteMutation = useMutation({
    mutationFn: (docId) => deleteEntityDocument(entityId, docId),
    onSuccess: onDeleted,
  });

  return (
    <div className="card">
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
        <div>
          <h2 className="font-semibold text-gray-900">Dokumen Lainnya</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            {docs.length} dokumen · dokumen dengan tipe bebas
          </p>
        </div>
        {isMaster && (
          <button
            onClick={() => setShowForm((v) => !v)}
            className="btn-primary flex items-center gap-1.5 text-xs py-1.5 px-3"
          >
            <Plus size={13} /> Tambah Dokumen
          </button>
        )}
      </div>

      {showForm && isMaster && (
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
          <p className="text-xs font-semibold text-gray-600 mb-3">Upload Dokumen Baru</p>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <div>
              <label className="label text-xs">Nama Dokumen</label>
              <input
                className="input text-sm"
                placeholder="e.g. Izin Lingkungan, HGB, SHM..."
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
              />
            </div>
            <div>
              <label className="label text-xs">Kategori</label>
              <select
                className="input text-sm"
                value={category}
                onChange={(e) => { setCategory(e.target.value); setValidUntil(""); }}
              >
                <option value="GENERAL">Dokumen Umum</option>
                <option value="PERIZINAN">Perizinan</option>
                <option value="KEUANGAN">Keuangan</option>
              </select>
            </div>
          </div>
          {category === "PERIZINAN" && (
            <div className="mb-2">
              <label className="label text-xs">Berlaku s/d <span className="text-gray-400 font-normal">(opsional)</span></label>
              <input
                type="date"
                className="input text-sm"
                value={validUntil}
                onChange={(e) => setValidUntil(e.target.value)}
              />
            </div>
          )}
          <div className="flex justify-end gap-2 mt-2">
            <button
              onClick={() => { setShowForm(false); setUploadError(null); }}
              className="btn-secondary text-xs py-2 px-3"
            >
              Batal
            </button>
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading || !customName.trim()}
              className="btn-primary text-xs py-2 px-4 flex items-center gap-1.5"
            >
              <Upload size={13} /> {uploading ? "Uploading..." : "Pilih File"}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.docx"
              className="hidden"
              onChange={handleFile}
            />
          </div>
          {uploadError && <p className="text-xs text-red-600 mt-2">{uploadError}</p>}
        </div>
      )}

      <div className="px-6 py-4">
        {docs.length === 0 ? (
          <div className="text-center py-8 border border-dashed border-gray-200 rounded-lg">
            <FileText size={24} className="text-gray-200 mx-auto mb-2" />
            <p className="text-sm text-gray-400">Belum ada dokumen lainnya</p>
          </div>
        ) : (
          <div className="space-y-4">
            {docs.map((doc) => (
              <div key={doc.id}>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                  {doc.customName || "Custom"}
                </p>
                <DocRow
                  doc={doc}
                  entityId={entityId}
                  isMaster={isMaster}
                  onDelete={() => deleteMutation.mutate(doc.id)}
                  onUpdated={onDeleted}
                  deleting={deleteMutation.isPending}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Pengurus card (current + history) ───────────────────────────────────────

const ROLE_LABELS = Object.fromEntries(
  [
    { key: "komisarisUtama", label: "Komisaris Utama" },
    { key: "komisaris",      label: "Komisaris" },
    { key: "direkturUtama",  label: "Direktur Utama" },
    { key: "direktur",       label: "Direktur" },
  ].map(({ key, label }) => [key, label])
);

function PengurusCard({ roles, pengurusData, history, isMaster, onEdit }) {
  const [showHistory, setShowHistory] = useState(false);
  const hasAny = roles.some((r) => pengurusData[r.key]?.name?.trim());
  const hasHistory = history?.length > 0;

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-gray-900">Struktur Pengurus</h2>
        <div className="flex items-center gap-2">
          {hasHistory && (
            <button
              onClick={() => setShowHistory((v) => !v)}
              className="text-xs text-gray-400 hover:text-gray-600"
            >
              {showHistory ? "Sembunyikan riwayat" : `Riwayat (${history.length})`}
            </button>
          )}
          {isMaster && (
            <button
              onClick={onEdit}
              className="flex items-center gap-1 text-xs text-brand-500 hover:underline"
            >
              <Pencil size={12} /> Edit
            </button>
          )}
        </div>
      </div>

      {hasAny ? (
        <dl className="space-y-3 text-sm">
          {roles.map(({ key, label }) => {
            const entry = pengurusData[key];
            const name = entry?.name?.trim();
            const tenure = calcTenure(entry?.startDate);
            return (
              <div key={key}>
                <dt className="text-xs text-gray-400 uppercase tracking-wide">{label}</dt>
                <dd className="mt-0.5">
                  {name ? (
                    <div>
                      <span className="text-gray-800 font-medium">{name}</span>
                      {entry?.startDate && (
                        <span className="ml-2 text-xs text-gray-400">
                          sejak {formatDate(entry.startDate)}
                          {tenure && <span className="ml-1 text-brand-500">· {tenure}</span>}
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-300 italic">—</span>
                  )}
                </dd>
              </div>
            );
          })}
        </dl>
      ) : (
        <div className="text-center py-4">
          <Users2 size={28} className="text-gray-200 mx-auto mb-2" />
          <p className="text-xs text-gray-400">Belum ada data pengurus</p>
          {isMaster && (
            <button onClick={onEdit} className="mt-2 text-xs text-brand-500 hover:underline">
              Tambah pengurus
            </button>
          )}
        </div>
      )}

      {showHistory && hasHistory && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Riwayat Pergantian
          </p>
          <div className="space-y-2">
            {[...history].reverse().map((entry, i) => (
              <div key={i} className="flex items-start gap-3 text-xs">
                <div className="w-1.5 h-1.5 rounded-full bg-gray-300 mt-1.5 flex-shrink-0" />
                <div>
                  <span className="font-medium text-gray-700">{entry.name}</span>
                  <span className="text-gray-400 ml-1">
                    sebagai {ROLE_LABELS[entry.role] ?? entry.role}
                  </span>
                  <div className="text-gray-400 mt-0.5">
                    {entry.startDate ? `${formatDate(entry.startDate)} – ` : ""}
                    {entry.endDate ? formatDate(entry.endDate) : ""}
                    {entry.startDate && entry.endDate && (
                      <span className="ml-1">
                        · {calcTenure(entry.startDate) ?? ""}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Summary card ─────────────────────────────────────────────────────────────

function SummaryCard({ entity, isMaster, onDone }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(entity.summary ?? "");
  const [error, setError] = useState(null);

  const save = useMutation({
    mutationFn: () => updateEntityInfo(entity.id, { summary: value }),
    onSuccess: () => {
      setEditing(false);
      setError(null);
      onDone();
    },
    onError: (err) => setError(err?.response?.data?.error ?? err.message),
  });

  const hasContent = entity.summary?.trim();

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <AlignLeft size={15} className="text-gray-400" />
          <h2 className="font-semibold text-gray-900">Ringkasan Entitas</h2>
        </div>
        {isMaster && !editing && (
          <button
            onClick={() => {
              setValue(entity.summary ?? "");
              setEditing(true);
            }}
            className="flex items-center gap-1 text-xs text-brand-500 hover:underline"
          >
            <Pencil size={12} /> {hasContent ? "Edit" : "Add summary"}
          </button>
        )}
      </div>

      {editing ? (
        <div className="space-y-3">
          {error && <ErrorMessage error={error} />}
          <textarea
            className="input text-sm w-full"
            rows={8}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={`Write a summary about this entity...\n\nSupports:\n- Long text\n- New lines\n- Bullet points (type them manually)`}
            autoFocus
          />
          <p className="text-xs text-gray-400">
            Use line breaks and bullet points (- or •) to structure the content.
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => save.mutate()}
              disabled={save.isPending}
              className="btn-primary flex items-center gap-2 text-sm"
            >
              <Save size={14} /> {save.isPending ? "Saving..." : "Save"}
            </button>
            <button
              onClick={() => setEditing(false)}
              className="btn-secondary text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : hasContent ? (
        <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
          {entity.summary}
        </p>
      ) : (
        <p className="text-sm text-gray-400 italic">
          No summary yet.{isMaster ? " Click 'Add summary' to write one." : ""}
        </p>
      )}
    </div>
  );
}

// ─── Entity identity card (name / code / group) ───────────────────────────────

function EntityIdentityCard({ entity, isMaster, onDone }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: "", code: "" });
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  const [error, setError] = useState(null);

  function startEdit() {
    setForm({
      name: entity.name ?? "",
      code: entity.code ?? "",
    });
    setError(null);
    setEditing(true);
  }

  const save = useMutation({
    mutationFn: () => updateEntityIdentity(entity.id, {
      name: form.name.trim(),
      code: form.code.trim(),
    }),
    onSuccess: () => {
      setEditing(false);
      setError(null);
      onDone();
    },
    onError: (err) => setError(err?.response?.data?.error ?? err.message),
  });

  const deactivate = useMutation({
    mutationFn: () => deactivateEntity(entity.id),
    onSuccess: () => {
      setConfirmDeactivate(false);
      onDone();
    },
    onError: (err) => setError(err?.response?.data?.error ?? err.message),
  });

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h2 className="font-semibold text-gray-900">Identitas Entitas</h2>
          {entity.managedByLegal && (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 bg-brand-50 border border-brand-100 rounded-full px-2 py-0.5">
              <ShieldCheck size={10} /> Legal
            </span>
          )}
        </div>
        {isMaster && !editing && (
          <button
            onClick={startEdit}
            className="flex items-center gap-1 text-xs text-brand-500 hover:underline"
          >
            <Pencil size={12} /> Edit
          </button>
        )}
      </div>

      {!editing ? (
        <>
          <dl className="space-y-2 text-sm">
            {[
              ["Nama", entity.name],
              ["Kode", entity.code],
              ["Group", entity.group?.name],
              ["Brand", entity.brand?.name],
            ].map(([label, value]) => (
              <div key={label}>
                <dt className="text-xs text-gray-400 uppercase tracking-wide">{label}</dt>
                <dd className="text-gray-800 mt-0.5 font-mono text-xs">
                  {value || <span className="text-gray-300 italic font-sans">—</span>}
                </dd>
              </div>
            ))}
            <div>
              <dt className="text-xs text-gray-400 uppercase tracking-wide">Status</dt>
              <dd className="mt-0.5">
                <span className={`inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full ${entity.isActive ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-400"}`}>
                  {entity.isActive ? "Aktif" : "Nonaktif"}
                </span>
              </dd>
            </div>
          </dl>

          {isMaster && entity.isActive && (
            <div className="mt-4 pt-3 border-t border-gray-100">
              {confirmDeactivate ? (
                <div className="text-xs text-gray-600">
                  <p className="mb-2 text-red-600 font-medium">Nonaktifkan entitas ini?</p>
                  {error && <p className="mb-2 text-red-600">{error}</p>}
                  <div className="flex gap-2">
                    <button
                      onClick={() => deactivate.mutate()}
                      disabled={deactivate.isPending}
                      className="text-xs text-red-600 hover:underline font-medium"
                    >
                      {deactivate.isPending ? "..." : "Ya, nonaktifkan"}
                    </button>
                    <button
                      onClick={() => setConfirmDeactivate(false)}
                      className="text-xs text-gray-400 hover:text-gray-600"
                    >
                      Batal
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setConfirmDeactivate(true)}
                  className="flex items-center gap-1 text-xs text-gray-400 hover:text-red-500 transition-colors"
                >
                  <PowerOff size={11} /> Nonaktifkan entitas
                </button>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="space-y-3">
          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </div>
          )}
          <div>
            <label className="label text-xs">Nama <span className="text-red-400">*</span></label>
            <input
              className="input text-sm"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              autoFocus
            />
          </div>
          <div>
            <label className="label text-xs">Kode <span className="text-gray-400 font-normal">max 10 chars</span></label>
            <input
              className="input text-sm font-mono"
              maxLength={10}
              value={form.code}
              onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
            />
          </div>
          <div className="flex gap-2 pt-1">
            <button
              onClick={() => { setError(null); save.mutate(); }}
              disabled={save.isPending || !form.name.trim()}
              className="btn-primary flex-1 flex items-center justify-center gap-2 text-sm"
            >
              <Save size={14} /> {save.isPending ? "Menyimpan..." : "Simpan"}
            </button>
            <button onClick={() => setEditing(false)} className="btn-secondary px-3">
              <X size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Editable info panel ──────────────────────────────────────────────────────

function EntityInfoPanel({ entity, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    email: entity.email ?? "",
    phone: entity.phone ?? "",
    address: entity.address ?? "",
    emailPassword: "",
  });
  const [showPwInput, setShowPwInput] = useState(false);
  const [revealedPw, setRevealedPw] = useState(null);
  const [revealing, setRevealing] = useState(false);
  const [error, setError] = useState(null);

  const save = useMutation({
    mutationFn: () => {
      const payload = { email: form.email, phone: form.phone, address: form.address };
      if (form.emailPassword) payload.emailPassword = form.emailPassword;
      return updateEntityInfo(entity.id, payload);
    },
    onSuccess: () => {
      setOpen(false);
      setError(null);
      setRevealedPw(null);
      onDone();
    },
    onError: (err) => setError(err),
  });

  async function reveal() {
    setRevealing(true);
    try {
      const { password } = await getEntityEmailPassword(entity.id);
      setRevealedPw(password);
    } catch (err) {
      setError(err?.response?.data?.error ?? err.message);
    } finally {
      setRevealing(false);
    }
  }

  function set(field) {
    return (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  }

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-gray-900">Informasi Perusahaan</h2>
        {!open && (
          <button
            onClick={() => { setOpen(true); setRevealedPw(null); }}
            className="flex items-center gap-1 text-xs text-brand-500 hover:underline"
          >
            <Pencil size={12} /> Edit
          </button>
        )}
      </div>

      {!open ? (
        <dl className="space-y-2 text-sm">
          {[
            ["Email", entity.email],
            ["No Telp", entity.phone],
            ["Alamat", entity.address],
          ].map(([label, value]) => (
            <div key={label}>
              <dt className="text-xs text-gray-400 uppercase tracking-wide">
                {label}
              </dt>
              <dd className="text-gray-800 mt-0.5 whitespace-pre-wrap">
                {value || <span className="text-gray-300 italic">Belum diisi</span>}
              </dd>
            </div>
          ))}

          {/* Email password row */}
          <div>
            <dt className="text-xs text-gray-400 uppercase tracking-wide flex items-center gap-1">
              <KeyRound size={11} /> Password Email
            </dt>
            <dd className="mt-0.5">
              {entity.hasEmailPassword ? (
                revealedPw ? (
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-mono text-gray-800 select-all">
                      {revealedPw}
                    </span>
                    <button
                      onClick={() => setRevealedPw(null)}
                      className="text-gray-300 hover:text-gray-500"
                    >
                      <EyeOff size={13} />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={reveal}
                    disabled={revealing}
                    className="flex items-center gap-1 text-xs text-brand-500 hover:underline"
                  >
                    <Eye size={12} /> {revealing ? "Memuat..." : "Tampilkan password"}
                  </button>
                )
              ) : (
                <span className="text-gray-300 italic text-sm">Belum diisi</span>
              )}
            </dd>
          </div>
        </dl>
      ) : (
        <div className="space-y-3">
          {error && <ErrorMessage error={error} />}
          {[
            ["Email", "email", "text", "legal@company.com"],
            ["No Telp", "phone", "tel", "+62 21 ..."],
            ["Alamat", "address", "textarea", "Jl. ..."],
          ].map(([label, field, type, placeholder]) => (
            <div key={field}>
              <label className="label text-xs">{label}</label>
              {type === "textarea" ? (
                <textarea
                  className="input text-sm"
                  rows={2}
                  value={form[field]}
                  onChange={set(field)}
                  placeholder={placeholder}
                />
              ) : (
                <input
                  className="input text-sm"
                  type={type}
                  value={form[field]}
                  onChange={set(field)}
                  placeholder={placeholder}
                />
              )}
            </div>
          ))}

          {/* Password email field */}
          <div>
            <label className="label text-xs flex items-center gap-1">
              <KeyRound size={11} /> Password Email
            </label>
            {showPwInput ? (
              <div className="relative">
                <input
                  className="input text-sm font-mono pr-8"
                  type="text"
                  value={form.emailPassword}
                  onChange={set("emailPassword")}
                  placeholder="Masukkan password email..."
                  autoComplete="off"
                />
                <button
                  onClick={() => { setShowPwInput(false); setForm((f) => ({ ...f, emailPassword: "" })); }}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500"
                >
                  <X size={13} />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowPwInput(true)}
                className="text-xs text-brand-500 hover:underline"
              >
                {entity.hasEmailPassword ? "Ganti password" : "Set password"}
              </button>
            )}
            {entity.hasEmailPassword && !showPwInput && (
              <p className="text-xs text-gray-400 mt-0.5">Password sudah tersimpan</p>
            )}
          </div>

          <div className="flex gap-2 pt-1">
            <button
              onClick={() => save.mutate()}
              disabled={save.isPending}
              className="btn-primary flex-1 flex items-center justify-center gap-2 text-sm"
            >
              <Save size={14} /> {save.isPending ? "Menyimpan..." : "Simpan"}
            </button>
            <button
              onClick={() => { setOpen(false); setShowPwInput(false); }}
              className="btn-secondary px-3"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function EntityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isMaster } = useAuth();
  const qc = useQueryClient();
  const [showShareholderModal, setShowShareholderModal] = useState(false);
  const [shareholderData, setShareholderData] = useState(null);
  const [showPengurusModal, setShowPengurusModal] = useState(false);
  const [pengurusData, setPengurusData] = useState(null);
  const [pengurusHistory, setPengurusHistory] = useState(null);

  const { data: entity, isLoading } = useQuery({
    queryKey: ["entity", id],
    queryFn: () => getEntity(id),
  });

  const effectiveShareholderData =
    shareholderData ?? entity?.shareholderData ?? [];
  const effectivePengurusData =
    normalizePengurus(pengurusData ?? entity?.pengurusData ?? {});
  const effectivePengurusHistory =
    pengurusHistory ?? entity?.pengurusHistory ?? [];

  const { data: vendorData } = useQuery({
    queryKey: ["vendors-for-entity", id],
    queryFn: () => getVendors({ entityId: id }),
    enabled: !!id,
    select: (res) => res?.data ?? res ?? [],
  });

  const { data: partnerData } = useQuery({
    queryKey: ["partners-for-entity", id],
    queryFn: () => getPartners({ entityId: id }),
    enabled: !!id,
    select: (res) => res?.data ?? res ?? [],
  });

  const vendorCount = vendorData?.length ?? 0;
  const partnerCount = partnerData?.length ?? 0;

  function invalidate() {
    qc.invalidateQueries({ queryKey: ["entity", id] });
  }

  if (isLoading) return <Spinner />;
  if (!entity) return <p className="text-gray-500">Entity not found.</p>;

  const allDocs = entity.documents ?? [];
  const docsByType = [...GENERAL_TYPES, ...PERIZINAN_TYPES].reduce((acc, t) => {
    acc[t] = allDocs.filter((d) => d.type === t);
    return acc;
  }, {});
  const laporanDocs = allDocs.filter((d) => d.type === "LAPORAN_KEUANGAN");
  const customDocs = allDocs.filter((d) => d.type === "CUSTOM");

  const activeCount = entity._count?.agreements ?? 0;
  const pastCount = entity._pastAgreementCount ?? 0;

  return (
    <div>
      {showShareholderModal && (
        <ShareholderModal
          entityId={id}
          initial={effectiveShareholderData}
          onClose={() => setShowShareholderModal(false)}
          onSaved={(data) => {
            setShareholderData(data ?? []);
            invalidate();
          }}
        />
      )}

      {showPengurusModal && (
        <PengurusModal
          entityId={id}
          initial={effectivePengurusData}
          onClose={() => setShowPengurusModal(false)}
          onSaved={(data, history) => {
            setPengurusData(data ?? {});
            setPengurusHistory(history ?? []);
            invalidate();
          }}
        />
      )}

      <Link
        to="/entities"
        className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"
      >
        <ChevronLeft size={15} /> Back to entities
      </Link>

      <PageHeader
        title={entity.name}
        subtitle={[entity.group?.name, entity.brand?.name, entity.code].filter(Boolean).join(" · ")}
      />

      {/* ── Stats row ──────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          ["Jumlah Karyawan", entity.employeeCount, "orang"],
          [
            "Perjanjian Aktif",
            activeCount,
            activeCount === 1 ? "agreement" : "agreements",
          ],
          [
            "Perjanjian Selesai",
            pastCount,
            pastCount === 1 ? "agreement" : "agreements",
          ],
        ].map(([label, value, unit]) => (
          <div key={label} className="card p-4 text-center">
            <p className="text-2xl font-bold text-gray-900">{value}</p>
            <p className="text-xs text-gray-500 mt-0.5">{unit}</p>
            <p className="text-xs font-medium text-gray-700 mt-1">{label}</p>
          </div>
        ))}
      </div>

      {/* ── Summary — full width below stats ───────────────────────────────── */}
      <div className="mb-6">
        <SummaryCard entity={entity} isMaster={isMaster} onDone={invalidate} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* ── Left sidebar ───────────────────────────────────────────────────── */}
        <div className="space-y-4">
          <EntityIdentityCard entity={entity} isMaster={isMaster} onDone={invalidate} />

          {isMaster ? (
            <EntityInfoPanel entity={entity} onDone={invalidate} />
          ) : (
            <div className="card p-5">
              <h2 className="font-semibold text-gray-900 mb-3">
                Informasi Perusahaan
              </h2>
              <dl className="space-y-2 text-sm">
                {[
                  ["Email", entity.email],
                  ["No Telp", entity.phone],
                  ["Alamat", entity.address],
                ].map(([label, value]) => (
                  <div key={label}>
                    <dt className="text-xs text-gray-400 uppercase tracking-wide">
                      {label}
                    </dt>
                    <dd className="text-gray-800 mt-0.5 whitespace-pre-wrap">
                      {value || <span className="text-gray-300 italic">—</span>}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          {/* Struktur Pengurus */}
          <PengurusCard
            roles={PENGURUS_ROLES}
            pengurusData={effectivePengurusData}
            history={effectivePengurusHistory}
            isMaster={isMaster}
            onEdit={() => setShowPengurusModal(true)}
          />

          <button
            onClick={() => navigate(`/agreements?entityId=${id}`)}
            className="w-full card p-4 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-brand-50 flex items-center justify-center">
                <FileText size={16} className="text-brand-500" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  Semua Perjanjian
                </p>
                <p className="text-xs text-gray-400">
                  {activeCount + pastCount} total
                </p>
              </div>
            </div>
            <ChevronLeft size={16} className="text-gray-400 rotate-180" />
          </button>

          <button
            onClick={() => navigate(`/vendors?entityId=${id}`)}
            className="w-full card p-4 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-teal-50 flex items-center justify-center">
                <Users size={16} className="text-teal-500" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  Semua Vendor
                </p>
                <p className="text-xs text-gray-400">
                  {vendorCount} vendor{vendorCount !== 1 ? "s" : ""} with
                  agreements
                </p>
              </div>
            </div>
            <ChevronLeft size={16} className="text-gray-400 rotate-180" />
          </button>

          <button
            onClick={() => navigate(`/partners?entityId=${id}`)}
            className="w-full card p-4 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-purple-50 flex items-center justify-center">
                <Users2 size={16} className="text-purple-500" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  Semua Partner
                </p>
                <p className="text-xs text-gray-400">
                  {partnerCount} partner{partnerCount !== 1 ? "s" : ""} with
                  agreements
                </p>
              </div>
            </div>
            <ChevronLeft size={16} className="text-gray-400 rotate-180" />
          </button>
        </div>

        {/* ── Right: docs + shareholder + laporan ────────────────────────────── */}
        <div className="lg:col-span-2 space-y-4">
          {/* General legal documents */}
          <div className="card">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="font-semibold text-gray-900">Dokumen Legal</h2>
              <p className="text-xs text-gray-400 mt-0.5">
                Akta dan NPWP single-file. Akta Perubahan &amp; SK Kemenkumham multi-file.
              </p>
            </div>
            <div className="px-6 py-4 space-y-6">
              {GENERAL_TYPES.map((type) => (
                <DocSection
                  key={type}
                  type={type}
                  category="GENERAL"
                  docs={docsByType[type] ?? []}
                  entityId={id}
                  isMaster={isMaster}
                  onUploaded={invalidate}
                  onDeleted={invalidate}
                />
              ))}
            </div>
          </div>

          {/* Perizinan documents */}
          <div className="card">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="font-semibold text-gray-900">Perizinan</h2>
              <p className="text-xs text-gray-400 mt-0.5">
                HAKI, NIB, PKKPR, Sertifikat Halal, PBG, SLF, dan lainnya. Semua multi-file.
              </p>
            </div>
            <div className="px-6 py-4 space-y-6">
              {PERIZINAN_TYPES.map((type) => (
                <DocSection
                  key={type}
                  type={type}
                  category="PERIZINAN"
                  docs={docsByType[type] ?? []}
                  entityId={id}
                  isMaster={isMaster}
                  onUploaded={invalidate}
                  onDeleted={invalidate}
                />
              ))}
            </div>
          </div>

          {/* Custom / other documents */}
          <CustomDocsCard
            docs={customDocs}
            entityId={id}
            isMaster={isMaster}
            onUploaded={invalidate}
            onDeleted={invalidate}
          />

          {/* Shareholder structure */}
          <div className="card">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-gray-900">
                  Struktur Pemegang Saham
                </h2>
                <p className="text-xs text-gray-400 mt-0.5">
                  Restricted to admin users only
                </p>
              </div>
              {isMaster && (
                <button
                  onClick={() => setShowShareholderModal(true)}
                  className="flex items-center gap-1.5 text-xs btn-secondary py-1.5 px-3"
                >
                  <Pencil size={12} /> Edit
                </button>
              )}
            </div>
            <div className="px-6 py-6">
              {effectiveShareholderData?.length > 0 ? (
                <ShareholderPieChart shareholders={effectiveShareholderData} />
              ) : (
                <div className="text-center py-6">
                  <PieChart size={32} className="text-gray-200 mx-auto mb-2" />
                  <p className="text-sm text-gray-400">
                    No shareholder data yet
                  </p>
                  {isMaster && (
                    <button
                      onClick={() => setShowShareholderModal(true)}
                      className="mt-3 text-xs text-brand-500 hover:underline"
                    >
                      Add shareholders
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Laporan Keuangan */}
          <LaporanKeuanganCard
            docs={laporanDocs}
            entityId={id}
            isMaster={isMaster}
            onUploaded={invalidate}
            onDeleted={invalidate}
          />
        </div>
      </div>
    </div>
  );
}
